import { computed } from "vue"

export const useSidebarNavigation = () => {
  /**
   * Persistent state stored in a cookie that survives page refreshes
   */
  const persistentOpen = useCookie<boolean>("sidebar_navigation_open", {
    default: () => true,
  })

  /**
   * Temporary state that overrides the cookie value when set
   * This allows for temporary UI adjustments without changing saved preferences
   */
  const temporaryState = useState<boolean | undefined>(
    "sidebar_navigation_temporary_state",
    () => undefined,
  )

  /**
   * The effective sidebar state, prioritizing temporary overrides
   * Falls back to the persistent cookie value when no override exists
   */
  const isOpen = computed(() => {
    return temporaryState.value ?? persistentOpen.value
  })

  /**
   * Default toggle function that changes the persistent state and clears temporary overrides
   * Changes are saved to the cookie and persist across page refreshes
   */
  const toggle = () => {
    persistentOpen.value = !persistentOpen.value
    temporaryState.value = undefined
  }

  /**
   * Temporarily toggles the sidebar without affecting the persistent state
   * Changes will be lost on page refresh
   */
  const toggleTemporarily = () => {
    temporaryState.value = !isOpen.value
  }

  /**
   * Resets to the persistent cookie state by clearing any temporary overrides
   */
  const resetToSaved = () => {
    temporaryState.value = undefined
  }

  /**
   * Temporarily opens the sidebar without affecting the persistent state
   */
  const openTemporarily = () => {
    temporaryState.value = true
  }

  /**
   * Temporarily closes the sidebar without affecting the persistent state
   */
  const closeTemporarily = () => {
    temporaryState.value = false
  }

  return {
    isOpen,
    toggle,
    toggleTemporarily,
    resetToSaved,
    openTemporarily,
    closeTemporarily,
  }
}
